/**
 * ExplanationSheet — bottom sheet that explains a tapped chart element.
 *
 * The core "wow" of the Mini App: tap a planet chip or an aspect on the natal
 * chart and a dignified, editorial-premium RU panel slides up to explain it.
 *
 * One component, two content modes:
 *   - mode "planet" → derives prose from PLANET_MEANINGS keyed by Planet.key,
 *     paired with the live ecliptic degree from the chart payload.
 *   - mode "aspect" → uses the AspectSummary the API already supplies
 *     (title + detail + kind), with a typed kind label.
 *
 * Reuses the existing `.chart-modal` / `.panel` / `.close-btn` shell (verbatim
 * mobile.html styling — instant reveal, no custom keyframe, so it is inherently
 * reduced-motion-safe like every other modal in the app). The few extra layout
 * bits (glyph badge, essence line, degree, grip) are inline-styled from the
 * existing `:root` design tokens so this component needs no global CSS edits.
 *
 * Keyboard: Escape closes; the close button is focused on open so screen-reader
 * / keyboard users land inside the dialog. Haptics fire via the legacy global
 * in lib/tg.ts (the `@telegram-apps/sdk-react` package is a dead dep — not
 * adopted here).
 */
import { type CSSProperties, type ReactElement, useEffect, useRef } from "react";

import type { AspectSummary, Planet } from "@/lib/api";
import { hapticSelection } from "@/lib/tg";

/** Visual + label map for the five aspect kinds the API returns. */
export const ASPECT_KIND: Record<
  AspectSummary["kind"],
  { readonly bg: string; readonly fg: string; readonly label: string }
> = {
  conjunction: { bg: "rgba(212,168,67,0.18)", fg: "#D4A843", label: "Соединение" },
  trine: { bg: "rgba(91,196,216,0.18)", fg: "#5BC4D8", label: "Тригон" },
  sextile: { bg: "rgba(159,186,217,0.18)", fg: "#9FBAD9", label: "Секстиль" },
  square: { bg: "rgba(216,91,91,0.18)", fg: "#D85B5B", label: "Квадратура" },
  opposition: { bg: "rgba(155,108,181,0.22)", fg: "#9B6CB5", label: "Оппозиция" },
};

/** Short aspect badge (used in lists where space is tight). */
export const ASPECT_SHORT: Record<AspectSummary["kind"], string> = {
  conjunction: "Соед.",
  trine: "Тригон",
  sextile: "Секст.",
  square: "Квадр.",
  opposition: "Оппоз.",
};

interface PlanetMeaning {
  /** Russian display name (chart payload carries the English `name`). */
  readonly ru: string;
  /** One-line essence — the planet's archetypal function. */
  readonly essence: string;
  /** Two–three sentences of dignified, non-fortune-telling interpretation. */
  readonly body: string;
}

/**
 * Editorial-premium meanings keyed by `Planet.key` (sun, moon, …). Calm,
 * literate Russian — describes the symbolism, never makes predictions or
 * medical/financial claims. Covers every key in the chart payload plus the
 * outer planets the engine adds later.
 */
const PLANET_MEANINGS: Record<string, PlanetMeaning> = {
  sun: {
    ru: "Солнце",
    essence: "Ядро личности и воля",
    body: "Солнце — центр карты: то, кем вы становитесь, когда перестаёте играть роли. Его знак и дом показывают, где вы светите естественнее всего и через что обретаете ощущение смысла. Это вектор зрелости, а не данность от рождения.",
  },
  moon: {
    ru: "Луна",
    essence: "Чувства и внутренний ритм",
    body: "Луна отвечает за то, что вам нужно, чтобы чувствовать себя в безопасности. Она правит привычками, памятью и первой, дословесной реакцией на мир. Её положение подсказывает, как вы заботитесь о себе и о близких.",
  },
  mercury: {
    ru: "Меркурий",
    essence: "Ум и речь",
    body: "Меркурий — это способ, которым вы думаете, говорите и связываете факты. Его знак окрашивает вашу логику: где-то стремительную, где-то обстоятельную. Он же отвечает за обучение, переписку и короткие дороги.",
  },
  venus: {
    ru: "Венера",
    essence: "Любовь, вкус и ценности",
    body: "Венера показывает, что вы считаете красивым и чего ищете в близости. Она правит притяжением, эстетикой и тем, как вы выражаете нежность. Её положение объясняет, что приносит вам удовольствие без усилия.",
  },
  mars: {
    ru: "Марс",
    essence: "Воля к действию",
    body: "Марс — это ваш способ желать и добиваться. Он отвечает за энергию, смелость и здоровую злость, которая защищает границы. Его знак подсказывает, как вы начинаете дела и за что готовы бороться.",
  },
  jupiter: {
    ru: "Юпитер",
    essence: "Рост и смысл",
    body: "Юпитер расширяет всё, к чему прикасается: знания, веру, возможности. Он отвечает за оптимизм и за чувство, что мир в целом дружелюбен. Его дом указывает область, где вам легче расти и где щедрость возвращается.",
  },
  saturn: {
    ru: "Сатурн",
    essence: "Структура и зрелость",
    body: "Сатурн — учитель границ и времени. Там, где он стоит, жизнь требует терпения и честной работы, зато даёт прочный, заслуженный результат. Это не наказание, а опора, которую вы выстраиваете сами.",
  },
  uranus: {
    ru: "Уран",
    essence: "Свобода и прорыв",
    body: "Уран отвечает за независимость и внезапные озарения. Он ломает то, что устарело, и открывает нестандартный взгляд на привычное. Его дом — область, где вам тесно в рамках и хочется поступать по-своему.",
  },
  neptune: {
    ru: "Нептун",
    essence: "Воображение и сострадание",
    body: "Нептун размывает границы между «я» и миром: отсюда мечты, искусство и тонкая интуиция. Он зовёт к состраданию и духовному поиску, но просит трезвости там, где легко обмануться. Его дом — место вашей чувствительности.",
  },
  pluto: {
    ru: "Плутон",
    essence: "Глубина и преображение",
    body: "Плутон отвечает за то, что меняет вас до основания. Он правит кризисами, которые очищают, и силой, которая рождается из пройденного. Его дом указывает, где вы переживаете самые глубокие трансформации.",
  },
};

/**
 * Fallback meaning for any key not in the table (e.g. asteroids or axis points
 * the engine may add). Keeps the sheet honest rather than guessing prose.
 */
function meaningFor(planet: Planet): PlanetMeaning {
  const known = PLANET_MEANINGS[planet.key];
  if (known) return known;
  return {
    ru: planet.name,
    essence: "Точка карты",
    body: "Подробное толкование этой точки появится в полном разборе карты в кабинете. Здесь показана её эклиптическая позиция, рассчитанная по Swiss Ephemeris.",
  };
}

export type SheetTarget =
  | { readonly kind: "planet"; readonly planet: Planet }
  | { readonly kind: "aspect"; readonly aspect: AspectSummary };

/* Inline styles built from the global :root design tokens — no global CSS edit. */
const headRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginBottom: 14,
};
const glyphBadge: CSSProperties = {
  flex: "none",
  width: 44,
  height: 44,
  display: "grid",
  placeItems: "center",
  borderRadius: 12,
  background: "rgba(212,168,67,0.12)",
  border: "1px solid rgba(212,168,67,0.22)",
  fontFamily: "serif",
  fontSize: 22,
  color: "var(--gold-bright)",
};
const titleCol: CSSProperties = { display: "flex", flexDirection: "column", gap: 3, minWidth: 0 };
const essenceLine: CSSProperties = {
  fontSize: 11,
  letterSpacing: "0.04em",
  color: "var(--fg-tertiary)",
};
const degreeTag: CSSProperties = {
  marginLeft: "auto",
  flex: "none",
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  color: "var(--gold-primary)",
};
const kindBadge: CSSProperties = {
  alignSelf: "flex-start",
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  padding: "2px 8px",
  borderRadius: 6,
};
const grip: CSSProperties = {
  display: "block",
  width: 36,
  height: 4,
  borderRadius: 2,
  background: "rgba(245,242,236,0.18)",
  margin: "0 auto 14px",
};

/**
 * Bottom-sheet dialog. Rendered only while `target` is non-null (the parent
 * unmounts it on close), reusing the `.chart-modal` shell.
 */
export function ExplanationSheet({
  target,
  onClose,
}: {
  readonly target: SheetTarget;
  readonly onClose: () => void;
}): ReactElement {
  const closeRef = useRef<HTMLButtonElement>(null);

  // Escape closes; focus the close button on open so keyboard / screen-reader
  // users land inside the dialog. Cleanup removes the listener on unmount.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const heading = target.kind === "planet" ? meaningFor(target.planet).ru : target.aspect.title;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click-to-close; keyboard users use Escape or the Закрыть button
    <div
      className="chart-modal"
      role="dialog"
      aria-modal="true"
      aria-label={heading}
      onClick={onClose}
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation only; no interactive intent */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: stops backdrop close when panel tapped */}
      <div className="panel" onClick={(e) => e.stopPropagation()}>
        <span style={grip} aria-hidden="true" />
        {target.kind === "planet" ? (
          <PlanetBody planet={target.planet} />
        ) : (
          <AspectBody aspect={target.aspect} />
        )}
        <button ref={closeRef} type="button" className="close-btn" onClick={onClose}>
          Закрыть
        </button>
      </div>
    </div>
  );
}

function PlanetBody({ planet }: { readonly planet: Planet }): ReactElement {
  const m = meaningFor(planet);
  return (
    <>
      <div style={headRow}>
        <span style={glyphBadge} aria-hidden="true">
          {planet.glyph}
        </span>
        <div style={titleCol}>
          <h3 style={{ margin: 0 }}>{m.ru}</h3>
          <span style={essenceLine}>{m.essence}</span>
        </div>
        <span style={degreeTag}>{planet.degree}</span>
      </div>
      <p>{m.body}</p>
    </>
  );
}

function AspectBody({ aspect }: { readonly aspect: AspectSummary }): ReactElement {
  const k = ASPECT_KIND[aspect.kind];
  return (
    <>
      <div style={{ ...headRow, alignItems: "flex-start" }}>
        <div style={titleCol}>
          <h3 style={{ margin: 0 }}>{aspect.title}</h3>
          <span style={{ ...kindBadge, background: k.bg, color: k.fg }}>{k.label}</span>
        </div>
      </div>
      <p>{aspect.detail}</p>
    </>
  );
}

/** Re-export the haptic the chart uses on tap, so callers import from one place. */
export { hapticSelection };
